<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import { PencilLine as PencilLineIcon, Trash2Icon } from 'lucide-vue-next'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu'
import ElementalTemplate from './ElementalTemplate/index.vue'
import { useElementalListContext } from '@/components/DrawerSidebarRight/components/shared/ElementalList/ElementalListContext'
import { type Elemental, ElementalTypeEnum, type ElementalTypeRoutes } from '~~/src/types/elemental'
import { useDeleteElemental } from '@//composables/mutations/elemetal/useDeleteElemental'
import SaveElementalMenuItem from '@/components/DrawerSidebarRight/components/shared/SaveElemental/index.vue'
import RemoveSaveElementalItem from '@/components/DrawerSidebarRight/components/shared/RemoveSaveElemental/index.vue'
import DownloadKnowledgeBase from '@/components/ElemetalMenu/DownloadKnowledgeBase/index.vue'
import CopyIdOption from '@/components/ElemetalMenu/CopyIdOption/index.vue'
import CopyElementalLinkOption from '@/components/ElemetalMenu/CopyElementalLinkOption/index.vue'
import OpenElementalLinkOption from '@/components/ElemetalMenu/OpenElementalLinkOption/index.vue'
import OpenTagBox from '@/components/ElemetalMenu/OpenTagBox/index.vue'
import OpenFormViewOption from '@/components/ElemetalMenu/OpenFormView/index.vue'
import OpenViewFeedOption from '@/components/ElemetalMenu/OpenViewFeedOption/index.vue'
import DuplicateElementalOption from '@/components/ElemetalMenu/DuplicateElementalOption/index.vue'
import { useUser } from '@/composables/auth.client'
import { useSidebarStore } from '~~/src/stores/sidebar'
import ElementalItemButton from '@/components/DrawerSidebarRight/components/shared/ElementalItemButton/index.vue'
import { useElementalFormStore } from '~~/src/stores/elementalForm'
import FavoriteElemental from '@/components/DrawerSidebarRight/components/shared/FavoriteElemental/index.vue'
import ElementalKnowledgeBase from '@/components/ElemetalMenu/ElementalKnowledgeBase/index.vue'
import { useConfirmDialog } from '@/composables/useConfirmDialog'

const { showEditAction = true, elemental } = defineProps<{
  elemental: Elemental
  isListElement?: boolean
  showEditAction?: boolean
  hiddeDuplicateAction?: boolean
  hiddeDeleteAction?: boolean
  hiddeCopyLinkAction?: boolean
  hiddeSaveAction?: boolean
  hiddeOpenExternalAction?: boolean
}>()

const { confirm } = useConfirmDialog()

const { mutateAsync } = useDeleteElemental()
const user = useUser()

const { listId, parentListId, isFolder, isAll } = useElementalListContext()

const insideListId = computed(() => {
  if (elemental.elementalTypeId === ElementalTypeEnum.List)
    return parentListId
  return isAll ? undefined : listId
})

const isOwner = computed(() => {
  return user.value?.id === elemental.user.id
})

function wordsFistLetterToUpperCase(str: string) {
  return str.replace(/\b\w/g, l => l.toUpperCase())
}

const router = useRouter()
const elementalLabel = getElementalTypeRoute(elemental.elementalTypeId)
const { open: openElementalForm, data: elementalFormData } = useElementalFormStore()
const { toggleSidebar, state: sidebarState, goToView } = useSidebarStore()
const closeSidebar = useDebounceFn(() => toggleSidebar(), 300)
const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const goToEditSidebar = () => {
  openElementalForm('edit', unref(elemental), getElementalType(elemental.elementalTypeId, elemental.isList), true)
}

function goToEditPage() {
  const elementalRouteId = elemental.elementalTypeId
  if (elementalRouteId === null)
    return
  router.push(`/${elementalLabel}/${elemental.id}/edit`)
  if (isMobile.value)
    closeSidebar()
}

const handleDelete = async () => {
  const confirmed = await confirm({
    icon: Trash2Icon,
    title: `Delete ${elementalLabel}`,
    description: 'Are you sure you want to delete this item? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'destructive',
  })

  if (!confirmed)
    return

  const elementalTypeId = elemental.elementalTypeId
  const elementalRouteId = elementalTypeId !== undefined ? elementalTypeId as keyof typeof ElementalTypeRoutes : null
  if (elementalRouteId === null)
    return
  try {
    await mutateAsync({
      elementalType: elementalRouteId,
      id: elemental.id,
      listId: insideListId.value,
      listType: elemental.elementalTypeId,
    }, {
      onSuccess: (result) => {
        if (!result)
          return

        if (elementalFormData?.id === elemental.id && sidebarState.mode === 'edit') {
          goToView()
          return
        }

        if (router.currentRoute.value.path !== '/library')
          router.push('/')
      },
    })
  }
  catch (error) {}
}

defineOptions({
  name: 'ElemetalMenu',
})

const [open, toggle] = useToggle()

onMounted(() => {

})
</script>

<template>
  <DropdownMenu :open="open" :class="$props.class" @update:open="toggle">
    <slot name="trigger" :toggle="toggle" :open="open">
      <DropdownMenuTrigger as-child>
        <ElementalItemButton title="More options">
          <div i-material-symbols:more-vert />
        </ElementalItemButton>
      </DropdownMenuTrigger>
    </slot>

    <DropdownMenuContent class="min-w-38">
      <DropdownMenuItem v-if="isOwner && showEditAction" @select="goToEditSidebar">
        <IconDockRight class="w-3 h-3 rotate-180" />
        Quick Edit
      </DropdownMenuItem>
      <DropdownMenuItem v-if="isOwner && showEditAction" @select="goToEditPage">
        <PencilLineIcon class="w-3 h-3" />
        {{ wordsFistLetterToUpperCase(`edit ${elementalLabel}`) }}
      </DropdownMenuItem>
      <ElementalTemplate v-if="isOwner" :elemental="elemental" />
      <ElementalKnowledgeBase v-if="isOwner" :elemental="elemental" />
      <DropdownMenuSeparator v-if="isOwner" />
      <DownloadKnowledgeBase :elemental="elemental" :is-table="elemental.elementalTypeId === ElementalTypeEnum.Table" />
      <CopyIdOption :elemental-id="elemental.id" />
      <CopyElementalLinkOption v-if="!hiddeCopyLinkAction" :elemental="elemental" :route="elementalLabel" />
      <DuplicateElementalOption
        v-if="user?.id && !hiddeDuplicateAction" :elemental="elemental"
        :route="elementalLabel" :list-id="insideListId"
      />
      <DropdownMenuSeparator v-if="isOwner" />
      <OpenTagBox
        v-if="isOwner" :elemental="elemental"
        :route="elementalLabel"
      />
      <DropdownMenuSeparator />
      <OpenElementalLinkOption
        v-if="!hiddeOpenExternalAction" :elemental="elemental"
        :route="elementalLabel"
      />
      <OpenFormViewOption
        v-if="elemental.elementalTypeId === ElementalTypeEnum.Table"
        :elemental="elemental"
        :route="elementalLabel"
      />
      <OpenViewFeedOption
        :elemental="elemental"
        :route="elementalLabel"
      />
      <DropdownMenuSeparator />
      <FavoriteElemental :elemental="elemental" />
      <SaveElementalMenuItem
        v-if="!isFolder && !elemental.isSaved && insideListId && !hiddeSaveAction"
        :id="elemental.id"
        :elemental-type="elemental.elementalTypeId"
        :list-id="insideListId"
      />
      <RemoveSaveElementalItem
        v-else-if="!hiddeSaveAction"
        :id="elemental.id"
        :elemental-type="elemental.elementalTypeId"
        :list-id="insideListId"
        :list-type="elemental.elementalTypeId"
      />
      <DropdownMenuItem v-if="isOwner && !hiddeDeleteAction" @click="handleDelete">
        <Trash2Icon w-3 h-3 />
        Delete
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
